import type { EcomPlatformViewerScriptContext } from '../../../../viewer-script/types';
import { ECOM_PLATFORM_APP_DEF_ID, PageMap } from '../utils/consts';
import { addWhiteListParamsToUrl } from '../utils/whiteListParams';
import { isValidHttpUrl } from '../utils/isValidHttpUrl';

export class NavigationService {
  private readonly continueShoppingUrl?: string;
  private readonly successUrl?: string;

  constructor(
    private readonly context: EcomPlatformViewerScriptContext,
    options: {
      continueShoppingUrl?: string;
      successUrl?: string;
    },
  ) {
    this.continueShoppingUrl = options?.continueShoppingUrl;
    this.successUrl = options?.successUrl;
  }

  public async getThankYouPageUrlForPlaceOrder(): Promise<string> {
    if (this.successUrl && isValidHttpUrl(this.successUrl)) {
      return this.successUrl.replace('{objectType}', 'order');
    }
    const { url } = await this.getSectionUrl(PageMap.THANK_YOU_PAGE);
    const orderIdPlaceholder = 'orderIdPlaceholder';
    const thankYouPageUrl = new URL(`${url}/${orderIdPlaceholder}`);
    const appSectionParams = JSON.stringify({
      objectType: 'order',
      origin: 'checkout',
      ...(this.continueShoppingUrl && {
        continueShoppingUrl: this.continueShoppingUrl,
      }),
    });
    thankYouPageUrl.searchParams.set('appSectionParams', appSectionParams);
    addWhiteListParamsToUrl(thankYouPageUrl, this.context.wixApi!.location.url);
    return thankYouPageUrl
      .toString()
      .replace('orderIdPlaceholder', '{orderId}');
  }

  public async getCheckoutErrorUrl(
    id: string,
    cashierPaymentId: string,
    contentAppId?: string,
    contentComponentId?: string,
  ): Promise<string> {
    const checkoutErrorUrl = await this.getCheckoutUrl(
      id,
      cashierPaymentId,
      contentAppId,
      contentComponentId,
    );
    checkoutErrorUrl.searchParams.set('redirect', 'error');
    addWhiteListParamsToUrl(
      checkoutErrorUrl,
      this.context.wixApi!.location.url,
    );
    return checkoutErrorUrl.toString();
  }

  public async getCheckoutCancelUrl(
    id: string,
    cashierPaymentId: string,
    contentAppId?: string,
    contentComponentId?: string,
  ): Promise<string> {
    const checkoutCancelUrl = await this.getCheckoutUrl(
      id,
      cashierPaymentId,
      contentAppId,
      contentComponentId,
    );
    checkoutCancelUrl.searchParams.set('redirect', 'cancel');
    return checkoutCancelUrl.toString();
  }

  public async getThankYouPageUrl(orderId: string): Promise<string> {
    if (this.successUrl && isValidHttpUrl(this.successUrl)) {
      return this.successUrl
        .replace('{orderId}', orderId)
        .replace('{objectType}', 'order');
    }
    const { url } = await this.getSectionUrl(PageMap.THANK_YOU_PAGE);
    const thankYouPageUrl = new URL(`${url}/${orderId}`);
    thankYouPageUrl.searchParams.set(
      'appSectionParams',
      JSON.stringify({
        objectType: 'order',
        origin: 'cashier-express-directly-to-typ',
        ...(this.continueShoppingUrl && {
          continueShoppingUrl: this.continueShoppingUrl,
        }),
      }),
    );
    return thankYouPageUrl.toString();
  }

  private async getCheckoutUrl(
    id: string,
    cashierPaymentId: string,
    contentAppId?: string,
    contentComponentId?: string,
  ): Promise<URL> {
    const { url } = await this.getSectionUrl(PageMap.CHECKOUT);
    const checkoutUrl = new URL(url!);

    const queryParams = {
      checkoutId: id,
      ...(this.continueShoppingUrl && {
        continueShoppingUrl: this.continueShoppingUrl,
      }),
      ...(this.successUrl && {
        successUrl: this.successUrl.replace('{objectType}', 'order'),
      }),
      cashierPaymentId,
      ...(contentAppId && { contentAppId }),
      ...(contentComponentId && { contentComponentId }),
    };

    Object.entries(queryParams).forEach(([key, value]) => {
      checkoutUrl.searchParams.set(key, value);
    });

    addWhiteListParamsToUrl(checkoutUrl, this.context.wixApi!.location.url);

    return checkoutUrl;
  }

  public async navigateToCheckout(
    id: string,
    cashierPaymentId: string,
  ): Promise<void> {
    const checkoutUrl: URL = await this.getCheckoutUrl(id, cashierPaymentId);
    return this.context.wixApi!.location.to?.(checkoutUrl.toString());
  }

  private getSectionUrl(sectionId: string): Promise<{ url?: string }> {
    const params = { sectionId, appDefinitionId: ECOM_PLATFORM_APP_DEF_ID };
    return this.context.wixApi!.site.getSectionUrl(params, false);
  }
}
